/* eslint-disable react-hooks/exhaustive-deps */
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react";

import { getItinerariesListAPI } from "../../api/itinerary/getSaveItinerariesList";
import useCustomErrorHandler from "../../hooks/useCustomErrorHandler";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import { GetItinerariesListResponse } from "../../interface";
import { StateContext } from "../globalContext/context";
import { iGlobalContext } from "../globalContext/interface";
import { iComputedTripDates, iCurrentTripContext } from "./currentTripContextInterface";

const CurrentTripContext = createContext({} as iCurrentTripContext);
export const CurrentTripContextProvider = ({ children }: { children: ReactNode }) => {
  const globalContext = useContext(StateContext);
  const { userInfo, accessJWT }: iGlobalContext = globalContext;
  const [currentTripData, setCurrentTripData] = useSessionStorage("_trip_trove_current_trip_data", {});
  // Latest summary-derived startDate/endDate for the trip currently shown in
  // TripDetailsPage. MyTripsCard reads this so its date range matches the
  // header for the same trip (gated by id).
  const [computedTripDates, setComputedTripDates] = useState<iComputedTripDates>(null);
  const { handleCustomError } = useCustomErrorHandler();
  async function getCurrentTrip(userId: string) {
    setCurrentTripData({});
    const response: GetItinerariesListResponse = await getItinerariesListAPI(accessJWT, userId, "current", 0, 10);
    if (response?.statusCode !== 200) {
      handleCustomError(response?.statusCode, "itinerary.list");
      return null;
    }
    const ownTrips = (response?.data || []).filter((d: any) => d?.shared_by_user_id == null);
    if (ownTrips.length > 0) {
      setCurrentTripData(ownTrips[0]);
      return true;
    }
    return false;
  }

  useEffect(() => {
    if (!userInfo?.id) {
      setCurrentTripData({});
      setComputedTripDates(null);
    }
  }, [userInfo]);

  const value = useMemo(
    () => ({
      currentTripData,
      setCurrentTripData,
      getCurrentTrip,
      computedTripDates,
      setComputedTripDates,
    }),
    [currentTripData, setCurrentTripData, getCurrentTrip, computedTripDates]
  );
  return <CurrentTripContext.Provider value={value}>{children}</CurrentTripContext.Provider>;
};
export const useCurrentTripContext = () => useContext(CurrentTripContext);
